/**
 * StockAdjustmentPrompt — acerta o estoque quando a QUANTIDADE de um item muda
 * depois da baixa (gatilho `quantity_changed`).
 *
 * A conta e o recorte (por que item novo NÃO entra aqui, e por que produto já
 * devolvido não volta a ser baixado) estão em `src/lib/stockReturn.ts`, junto do
 * `quantityAdjustmentLines`. Aqui só há I/O e tela.
 *
 * Duas coisas que este componente faz igual aos irmãos e por bons motivos:
 *  - **Nunca usa a quantidade do documento como verdade do que saiu.** O que
 *    saiu está nos movimentos; o documento diz o que se quer HOJE. A diferença
 *    é o acerto.
 *  - **O modo automático espera a consulta RESOLVER.** Sem isso, uma lista
 *    vazia "em voo" e uma lista vazia "de verdade" seriam indistinguíveis, e um
 *    render intermediário poderia disparar um acerto com dados incompletos.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Scale, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useRole } from '@/hooks/useRole';
import { supabase } from '@/integrations/supabase/client';
import { useRegisterStockMovement } from '@/hooks/useStock';
import { useStockSettings } from '@/hooks/useStockSettings';
import {
  DOCUMENT_STOCK_REASONS,
  documentChangedAfterMovements,
  quantityAdjustmentLines,
  type DocumentQuantityItem,
  type StockMovementLike,
} from '@/lib/stockReturn';
import { plural } from '@/lib/plural';
import { formatStockQuantity } from '@/lib/stock';

interface Props {
  /** Itens GRAVADOS do documento (com product_id + quantity). */
  items: DocumentQuantityItem[];
  /**
   * `updated_at` do documento. É o que separa "a quantidade mudou depois da
   * baixa" de "o usuário baixou menos de propósito" — ver
   * `documentChangedAfterMovements`. Sem ele, o convite nunca aparece.
   */
  documentUpdatedAt?: string | null;
  /** Exatamente um destes: origem da baixa. */
  quoteId?: string | null;
  serviceOrderId?: string | null;
}

const dismissKey = (kind: string, id: string) => `orcamigo_stock_adjusted_${kind}_${id}`;

/**
 * O "não quero acertar" é guardado com a ASSINATURA da divergência, não só com
 * o id do documento.
 *
 * Um marcador por documento esconderia para sempre qualquer acerto futuro
 * daquele orçamento — o usuário dispensa uma diferença de 2 unidades e nunca
 * mais fica sabendo da de 30. Com a assinatura, dispensar vale para ESTA
 * divergência; mudar a quantidade de novo faz o convite voltar.
 */
function discrepancySignature(lines: readonly { product_id: string; delta: number }[]): string {
  return lines.map((line) => `${line.product_id}:${line.delta}`).join('|');
}

export function StockAdjustmentPrompt({
  items,
  documentUpdatedAt,
  quoteId,
  serviceOrderId,
}: Props) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const inventory = useFeature('inventory');
  const { canWrite } = useRole();
  const register = useRegisterStockMovement();
  const queryClient = useQueryClient();
  const { actionFor } = useStockSettings();
  const autoRan = useRef(false);

  const kind = quoteId ? 'quote' : 'so';
  const sourceId = (quoteId ?? serviceOrderId ?? '') as string;
  const isRealSource = Boolean(sourceId) && !String(sourceId).startsWith('offline_');
  const action = actionFor('quantity_changed');
  /** O motivo da baixa complementar acompanha a origem do documento. */
  const saleReason = quoteId ? ('quote_sale' as const) : ('service_order_sale' as const);

  /** Já acertou/dispensou NESTA sessão de tela. */
  const [dismissed, setDismissed] = useState(false);
  const [dismissedSignature] = useState(() => {
    try {
      return localStorage.getItem(dismissKey(kind, sourceId)) ?? '';
    } catch {
      return '';
    }
  });

  const queryKey = ['stock-adjustment', kind, sourceId, activeCompany?.id];
  const { data: movements, isSuccess: guardResolved } = useQuery({
    queryKey,
    enabled:
      Boolean(user?.id && activeCompany?.id) &&
      inventory.allowed &&
      isRealSource &&
      action !== 'off' &&
      !dismissed,
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    queryFn: async (): Promise<(StockMovementLike & { created_at?: string | null })[]> => {
      let request = supabase
        .from('stock_movements')
        .select('product_id, delta, reason, created_at')
        .eq('company_id', activeCompany!.id)
        .in('reason', DOCUMENT_STOCK_REASONS as unknown as string[]);
      request = quoteId
        ? request.eq('quote_id', quoteId)
        : request.eq('service_order_id', serviceOrderId!);
      const { data, error } = await request;
      if (error) throw error;
      return (data ?? []) as (StockMovementLike & { created_at?: string | null })[];
    },
  });

  /** Baixar menos de propósito ≠ mudar a quantidade depois de baixar. */
  const changedAfterDeduction = useMemo(
    () => documentChangedAfterMovements(documentUpdatedAt, movements ?? []),
    [documentUpdatedAt, movements],
  );

  const lines = useMemo(
    () => (changedAfterDeduction ? quantityAdjustmentLines(movements ?? [], items ?? []) : []),
    [changedAfterDeduction, movements, items],
  );

  const signature = useMemo(() => discrepancySignature(lines), [lines]);

  const canAct =
    inventory.allowed &&
    canWrite &&
    isRealSource &&
    action !== 'off' &&
    lines.length > 0 &&
    // O modo automático ignora a dispensa: quem escolheu "Automático" não
    // quer decidir caso a caso. A dispensa é um gesto do modo "Perguntar".
    (action === 'auto' || signature !== dismissedSignature);

  const runAdjustment = async (opts?: { auto?: boolean }) => {
    if (lines.length === 0) return;
    try {
      for (const line of lines) {
        await register.mutateAsync({
          productId: line.product_id,
          // `delta` já vem assinado: positivo devolve, negativo baixa mais.
          delta: line.delta,
          reason: line.delta > 0 ? 'sale_return' : saleReason,
          quoteId: quoteId ?? null,
          serviceOrderId: serviceOrderId ?? null,
          note: 'Acerto por quantidade alterada',
        });
      }
      queryClient.invalidateQueries({ queryKey });
      toast.success(
        opts?.auto
          ? `Acerto automático de estoque registrado (${plural(lines.length, 'produto', 'produtos')}).`
          : 'Estoque acertado.',
      );
      setDismissed(true);
    } catch {
      // erro individual já exibido pela mutation
    }
  };

  // ⚠️ Hook DEPOIS de `runAdjustment` (TDZ, CLAUDE.md #92) e ANTES do `return
  // null` (ordem de hooks). O modo automático é justamente o caminho que sai
  // antes do JSX.
  useEffect(() => {
    if (action !== 'auto' || !guardResolved || !canAct || autoRan.current || register.isPending) {
      return;
    }
    autoRan.current = true;
    void runAdjustment({ auto: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action, guardResolved, canAct, register.isPending]);

  if (!canAct || dismissed || action !== 'ask') return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(dismissKey(kind, sourceId), signature);
    } catch {
      // ignore
    }
    setDismissed(true);
  };

  const returns = lines.filter((line) => line.delta > 0).length;
  const deductions = lines.length - returns;

  return (
    <Card className="border-violet-200 bg-violet-50/60 dark:border-violet-900/50 dark:bg-violet-950/20">
      <CardContent className="flex items-center gap-3 p-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-violet-100 dark:bg-violet-950/40">
          <Scale className="h-5 w-5 text-violet-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Acertar o estoque?</p>
          <p className="text-xs text-muted-foreground">
            A quantidade mudou depois da baixa:{' '}
            {[
              deductions > 0 && `${plural(deductions, 'produto a baixar', 'produtos a baixar')}`,
              returns > 0 && `${plural(returns, 'produto a devolver', 'produtos a devolver')}`,
            ]
              .filter(Boolean)
              .join(' e ')}
            .
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {lines
              .map(
                (line) =>
                  `${formatStockQuantity(line.deducted)} → ${formatStockQuantity(line.desired)}`,
              )
              .join(' · ')}
          </p>
        </div>
        <Button3D size="sm" onClick={() => runAdjustment()} disabled={register.isPending}>
          Acertar
        </Button3D>
        <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={handleDismiss}>
          <X className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}
